import { createDeepEqualSelector, RootState } from '@/store/store';
import { HypertrackerState } from '@/state/hypertracker/hypertrackerTypes';
import { ALERT_PLANS } from '@/state/payments/paymentsTypes';

const hypertrackerSelector = (state: RootState): HypertrackerState => state.hypertracker;

export const selectHypertrackerFilters = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.filters;
    },
);

export const selectModalWindow = createDeepEqualSelector([hypertrackerSelector], (hypertracker) => {
    return hypertracker.modalWindow;
});
export const selectIsLoadingDisplayX = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.isLoadingDisplayX;
    },
);

export const selectHeatmapSelectedRow = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.heatmapSelectedRow;
    },
);
export const selectHeatmapSelectedField = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.heatmapSelectedField;
    },
);

export const selectTempKeyData = createDeepEqualSelector([hypertrackerSelector], (hypertracker) => {
    return hypertracker.tempKeyData;
});

export const selectConnections = createDeepEqualSelector([hypertrackerSelector], (hypertracker) => {
    return hypertracker.connections;
});

export const selectVaults = createDeepEqualSelector([hypertrackerSelector], (hypertracker) => {
    return hypertracker.vaults;
});

export const selectPerpsCurrentAsset = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.perpsCurrentAsset;
    },
);

export const selectPositionsToExplore = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.perpsPositionsToExplore;
    },
);

export const selectPositionExplorerFilters = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.positionExplorerFilters;
    },
);

export const selectLeaderboardsTableFilters = createDeepEqualSelector(
    [hypertrackerSelector, (_, props: { key: string }) => props.key],
    (hypertracker, key) => {
        return hypertracker?.leaderboardsTableFilters?.[key] || null;
    },
);

export const selectBuilderMainChartView = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.builderMainChartView;
    },
);
export const selectBuildersTableRange = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.buildersTableRange;
    },
);
export const selectPersonalBuilderRange = createDeepEqualSelector(
    [hypertrackerSelector],
    (hypertracker) => {
        return hypertracker.personalBuilderRange;
    },
);

export const selectBuildersForSearch = createDeepEqualSelector(
    [hypertrackerSelector],
    (selector) => selector.buildersForSearch,
);
export const selectLeaderboardsLimit = createDeepEqualSelector(
    [hypertrackerSelector],
    (selector) => {
        return selector.leaderboardsTableFilters.limit;
    },
);

export const selectPositionRecencyToggle = createDeepEqualSelector(
    [hypertrackerSelector],
    ({ positionRecencyToggle }) => positionRecencyToggle,
);

export const selectPerpsPositionAge = createDeepEqualSelector(
    [hypertrackerSelector],
    ({ perpsPositionAge }) => perpsPositionAge,
);

export const selectLeaderboardsPerformanceData = createDeepEqualSelector(
    [hypertrackerSelector],
    ({ leaderboardsPerformanceData }) => leaderboardsPerformanceData,
);

export const selectLeaderboardsBehaviouralData = createDeepEqualSelector(
    [hypertrackerSelector],
    ({ leaderboardsBehaviouralData }) => leaderboardsBehaviouralData,
);

export const selectLeaderboardsSocialData = createDeepEqualSelector(
    [hypertrackerSelector],
    ({ leaderboardsSocialData }) => leaderboardsSocialData,
);

export const selectLeaderboardData = createDeepEqualSelector(
    [hypertrackerSelector, (_, props: { key: string }) => props.key],
    (hypertracker, key) => {
        return hypertracker?.leaderboards?.[key] || null;
    },
);

export const selectMe = createDeepEqualSelector([hypertrackerSelector], (selector) => {
    const updatedMe = { ...selector.me };
    if (
        updatedMe?.accountTypeSource === 'hyperliquid-referral' &&
        updatedMe?.accountType === ALERT_PLANS.BASED_UNLIMITED
    ) {
        updatedMe.accountType = ALERT_PLANS.HYPERLIQUID;
    }

    return updatedMe;
});
export const selectTelegramConnectionHT = createDeepEqualSelector(
    [hypertrackerSelector],
    (selector) => selector.telegramConnectionHT,
);

export const selectMarketRadarActiveTab = createDeepEqualSelector(
    [hypertrackerSelector],
    (selector) => selector.marketRadarActiveTab,
);

export const selectMarketRadarSelectedCoin = createDeepEqualSelector(
    [hypertrackerSelector],
    (selector) => selector.marketRadarSelectedCoin,
);

export const selectMarketRadarSelectedColor = createDeepEqualSelector(
    [hypertrackerSelector],
    (selector) => selector.marketRadarSelectedColor,
);

export const selectMarketRadarLimitOrderType = createDeepEqualSelector(
    [hypertrackerSelector],
    (selector) => selector.marketRadarLimitOrderType,
);

export const selectMarketRadarSelectedCandle = createDeepEqualSelector(
    [hypertrackerSelector],
    (selector) => selector.marketRadarSelectedCandle,
);

export const selectFavoriteCoinsList = createDeepEqualSelector(
    [hypertrackerSelector],
    (selector) => selector.favoriteCoinsList,
);

export const selectAlphafeedSelectedWallet = createDeepEqualSelector(
    [hypertrackerSelector],
    // Older persisted state has no key yet.
    (selector) => selector.alphafeedSelectedWallet ?? null,
);
